/**
 * Como se nombra cada estado de un pedido en el backoffice (ADM-09). Lo usan
 * la tabla del historial, sus filtros y el modal del detalle: el mismo estado
 * no puede llamarse de dos maneras en la misma pantalla.
 */
import type {
  CorreoPedidoAdmin,
  EstadoPedido,
  EstadoSincronizacionRandom,
  Pedido,
  PedidoAdminDetalle,
  TipoCorreoPedido,
} from '@elcarpintero/compartido';

import { numero } from './formato';

export interface Rotulo {
  clase: string;
  texto: string;
}

export const ESTADO_PAGO: Record<EstadoPedido, Rotulo> = {
  pagado: { clase: 'etq etq-ok', texto: 'Pagado' },
  pendiente_pago: { clase: 'etq etq-neutra', texto: 'Sin pagar' },
  rechazado: { clase: 'etq etq-critico', texto: 'Pago rechazado' },
  anulado: { clase: 'etq etq-neutra', texto: 'Anulado' },
};

/**
 * "Esperando a Random" no es una falla: la integracion es por consulta y un
 * pedido recien pagado se queda ahi hasta que Random pase a buscarlo. Lo que
 * si es una falla es que se quede demasiado (`sin_tomar`).
 */
export const SINCRONIZACION: Record<EstadoSincronizacionRandom, Rotulo> = {
  facturado: { clase: 'etq etq-ok', texto: 'Facturado' },
  registrado: { clase: 'etq etq-ok', texto: 'Nota de venta' },
  pendiente: { clase: 'etq etq-neutra', texto: 'Esperando a Random' },
  sin_tomar: { clase: 'etq etq-critico', texto: 'No llegó a Random' },
};

/**
 * Un pedido sin pagar tambien figura `pendiente` en Random, pero no espera
 * nada: Random solo consulta los pagados. Se dice eso y no "esperando".
 */
export function rotuloRandom(pedido: Pick<Pedido, 'estado' | 'estadoSincronizacionRandom'>): Rotulo {
  if (pedido.estado !== 'pagado' && pedido.estadoSincronizacionRandom === 'pendiente') {
    return { clase: 'etq etq-neutra', texto: 'No corresponde' };
  }
  return SINCRONIZACION[pedido.estadoSincronizacionRandom];
}

export function documento(pedido: Pick<Pedido, 'tipoDocumento' | 'folioDocumento'>): string {
  const tipo = pedido.tipoDocumento === 'factura' ? 'Factura' : 'Boleta';
  return pedido.folioDocumento !== null ? `${tipo} ${pedido.folioDocumento}` : `${tipo}, sin folio`;
}

/** Codigo de Transbank del medio de pago. Webpay Plus no entrega la marca de la tarjeta. */
const TIPO_PAGO: Record<string, string> = {
  VD: 'Débito',
  VP: 'Prepago',
  VN: 'Crédito sin cuotas',
  VC: 'Crédito en cuotas',
  SI: 'Crédito, 3 cuotas sin interés',
  S2: 'Crédito, 2 cuotas sin interés',
  NC: 'Crédito, cuotas sin interés',
};

export function tipoPago(codigo: string): string {
  return TIPO_PAGO[codigo] ?? codigo;
}

/** Dice "Correo de…": "Confirmacion de compra" a secas se leia como un tramite del cliente. */
export const TIPO_CORREO: Record<TipoCorreoPedido, string> = {
  confirmacion_pedido: 'Correo de confirmación de compra',
  documento_emitido: 'Correo con la boleta o factura',
  acceso_administrador: 'Acceso al panel',
  recuperacion_clave: 'Recuperación de clave',
  alerta_pedido_sin_registrar: 'Alerta: no llegó a Random',
  aviso_venta: 'Aviso de compra a la tienda',
};

/**
 * Estado de un correo en la bitacora. `pendiente` es que todavia no sale (el
 * envio apagado o en curso): se dice "Por enviar" y no "Pendiente", que al
 * lado de "Confirmacion de compra" parecia esperar algo del cliente.
 */
export function estadoCorreo(correo: Pick<CorreoPedidoAdmin, 'estado' | 'intentos'>): Rotulo {
  if (correo.estado === 'enviado') return { clase: 'etq etq-ok', texto: 'Enviado' };
  if (correo.estado === 'fallido') {
    return { clase: 'etq etq-critico', texto: `No se pudo enviar (${correo.intentos} intentos)` };
  }
  return { clase: 'etq etq-neutra', texto: 'Por enviar' };
}

/** "(3 unidades)", junto al titulo de lo comprado. */
export function unidadesPedido(pedido: Pick<PedidoAdminDetalle, 'items'>): string {
  const unidades = pedido.items.reduce((suma, item) => suma + item.cantidad, 0);
  return `(${numero(unidades)} ${unidades === 1 ? 'unidad' : 'unidades'})`;
}
